const MOBILE_QUERY = '(max-width: 749px)';

class CampaignStickyBuyBar extends HTMLElement {
  #abortController = new AbortController();
  #intersectionObserver;
  #buttonObserver;
  #mediaQuery = window.matchMedia(MOBILE_QUERY);
  #primaryButton;
  #primaryIsVisible = true;
  #viewportFrame;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    const primaryButtonId = this.dataset.primaryButtonId;
    const primaryButton = primaryButtonId ? document.getElementById(primaryButtonId) : null;
    if (!(primaryButton instanceof HTMLButtonElement)) return;

    this.#primaryButton = primaryButton;
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section') || document;

    this.#intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
      threshold: 0.15,
    });
    this.#intersectionObserver.observe(primaryButton);

    this.#buttonObserver = new MutationObserver(this.#syncButtonState);
    this.#buttonObserver.observe(primaryButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-busy'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    section.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    this.#mediaQuery.addEventListener('change', this.#handleMediaChange, { signal });
    window.addEventListener('scroll', this.#handleViewportChange, { passive: true, signal });
    window.addEventListener('resize', this.#handleViewportChange, { passive: true, signal });

    this.#syncButtonState();
    this.#syncSummary();
    this.#updateVisibility();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#intersectionObserver?.disconnect();
    this.#buttonObserver?.disconnect();
    if (this.#viewportFrame) window.cancelAnimationFrame(this.#viewportFrame);
    this.removeAttribute('data-visible');
    this.hidden = true;
    this.#syncDocumentState();
  }

  #handleIntersection = ([entry]) => {
    this.#primaryIsVisible = Boolean(entry?.isIntersecting);
    this.#updateVisibility();
  };

  #handleMediaChange = () => this.#updateVisibility();

  #handleViewportChange = () => {
    if (this.#viewportFrame) return;
    this.#viewportFrame = window.requestAnimationFrame(() => {
      this.#viewportFrame = undefined;
      if (!this.#primaryButton) return;
      const rect = this.#primaryButton.getBoundingClientRect();
      this.#primaryIsVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      this.#updateVisibility();
    });
  };

  #handleVariantUpdate = (event) => {
    const productId = String(event.detail?.data?.newProduct?.id || event.detail?.data?.productId || '');
    if (productId && productId !== this.dataset.productId) return;

    window.requestAnimationFrame(() => {
      this.#syncSummary(event.detail?.resource);
      this.#syncButtonState();
    });
  };

  #updateVisibility() {
    const shouldShow = this.#mediaQuery.matches && !this.#primaryIsVisible;
    this.hidden = !shouldShow;
    if (shouldShow) this.setAttribute('data-visible', 'true');
    else this.removeAttribute('data-visible');
    this.#syncDocumentState();
  }

  #syncDocumentState() {
    const hasVisibleBar = Boolean(document.querySelector('campaign-sticky-buy-bar[data-visible="true"]'));
    document.documentElement.classList.toggle('campaign-sticky-buy-bar-visible', hasVisibleBar);
  }

  #syncButtonState = () => {
    const proxyButton = this.querySelector('[data-campaign-buy-proxy]');
    if (!(proxyButton instanceof HTMLButtonElement) || !this.#primaryButton) return;

    const isBusy = this.#primaryButton.getAttribute('aria-busy') === 'true';
    proxyButton.disabled = this.#primaryButton.disabled;
    if (isBusy) proxyButton.setAttribute('aria-busy', 'true');
    else proxyButton.removeAttribute('aria-busy');
    proxyButton.textContent = isBusy ? 'AGREGANDO…' : 'QUIERO RECIBIRLO — PAGO AL RECIBIR';
  };

  #syncSummary(variant) {
    const section = this.closest('.shopify-section') || document;
    const variantElement = this.querySelector('[data-campaign-sticky-variant]');
    const priceElement = this.querySelector('[data-campaign-sticky-price]');
    const selectedCard = section.querySelector('.variant-picker--campaign input:checked')?.closest('.campaign-variant-card');
    const currentPrice = section.querySelector(`product-price[data-product-id="${CSS.escape(this.dataset.productId || '')}"] .price`);

    if (variantElement) {
      const selectedTitle = selectedCard?.getAttribute('data-campaign-variant-title');
      const variantTitle = variant?.title === 'Default Title' ? '' : variant?.title;
      const nextTitle = variantTitle || selectedTitle;
      if (nextTitle) variantElement.textContent = nextTitle;
    }

    if (priceElement) {
      const selectedPrice = selectedCard?.getAttribute('data-campaign-variant-price');
      const nextPrice = currentPrice?.textContent?.trim() || selectedPrice;
      if (nextPrice) priceElement.textContent = nextPrice;
    }
  }
}

if (!customElements.get('campaign-sticky-buy-bar')) {
  customElements.define('campaign-sticky-buy-bar', CampaignStickyBuyBar);
}

if (!window.__bodegaCampaignBuyProxyBound) {
  window.__bodegaCampaignBuyProxyBound = true;
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const proxyButton = event.target.closest('[data-campaign-buy-proxy]');
    if (!(proxyButton instanceof HTMLButtonElement) || proxyButton.disabled) return;

    const primaryButtonId = proxyButton.closest('campaign-sticky-buy-bar')?.dataset.primaryButtonId;
    const primaryButton = primaryButtonId
      ? document.getElementById(primaryButtonId)
      : document.querySelector('.buy-buttons-block--campaign [data-campaign-primary-buy]');

    if (!(primaryButton instanceof HTMLButtonElement) || primaryButton.disabled) return;

    event.preventDefault();
    primaryButton.click();
  });
}
